import {
  TicketDepartment,
  TicketsService,
  TicketType,
} from '@/@types/generated'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import format from '@/lib/format'
import { ChevronDownIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'

export interface TicketTypesSelectProps {
  department?: TicketDepartment
  value: TicketType[]
  onChange: (value: TicketType[]) => void
}

export default function TicketTypesSelect({
  department,
  value,
  onChange,
  children,
  ...props
}: React.PropsWithChildren<TicketTypesSelectProps> &
  Omit<React.ComponentProps<'button'>, keyof TicketTypesSelectProps>) {
  // State
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState<TicketType[]>([])

  // Computed
  const label = 'Ticket Types'
  const filteredOptions = options.filter(({ name }) =>
    name.toLowerCase().includes(filter)
  )

  const handleCheckedChange = (checked: boolean, id: number) => {
    // Checking
    if (checked) {
      const newVal = [...(value || []), options.find((o) => o.id === id)!]
      return onChange(newVal)
    }

    // Remove from array
    const newVal = value.filter((tt) => tt.id !== id)
    if (newVal.length === 0) return onChange([])
    onChange(newVal)
  }

  // Load ticket types from API
  useEffect(() => {
    setLoading(true)
    TicketsService.listTicketTypes(department)
      .then((res) => {
        setOptions(res.ticketTypes || [])
      })
      .finally(() => setLoading(false))
  }, [department])

  // Group types by department
  const groupedFilteredOptions = filteredOptions.reduce(
    (acc, tt) => {
      const dept = tt.department
      if (!acc[dept]) acc[dept] = []
      acc[dept].push(tt)
      return acc
    },
    {} as Record<TicketDepartment, TicketType[]>
  )

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children || (
          <Button variant="outline" {...props}>
            {label}
            {value.length > 0 && (
              <Badge className="rounded-full p-0 px-1 text-xs">
                {value.length}
              </Badge>
            )}
            <ChevronDownIcon className="size-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel className="flex items-center justify-between">
          {label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div onKeyDown={(e) => e.stopPropagation()}>
          <Input
            type="text"
            placeholder="Filter..."
            className="mb-2 w-full rounded border px-2 py-1 text-sm"
            onChange={(e) => setFilter(e.target.value.toLowerCase())}
            value={filter}
          />
        </div>

        {loading ? (
          <div className="text-muted-foreground p-2 text-sm">Loading...</div>
        ) : (
          <>
            {!filteredOptions.length && (
              <div className="text-muted-foreground p-2 text-sm">
                No results found.
              </div>
            )}
            {Object.entries(groupedFilteredOptions).map(([dept, types]) => (
              <DropdownMenuGroup key={dept}>
                <DropdownMenuLabel className="text-muted-foreground">
                  {format.Enum(TicketDepartment, dept as TicketDepartment)}
                </DropdownMenuLabel>
                {types.map(({ id, name }) => (
                  <DropdownMenuCheckboxItem
                    key={id}
                    checked={value.some((tt) => tt.id === id)}
                    onCheckedChange={(v) => handleCheckedChange(v, id)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {name}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuGroup>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
